/**
 * The settings information architecture.
 *
 * Every category belongs to exactly one scope, and the scope is the first question an
 * operator has about a setting: who does this change for? So the screen is organised
 * scope-first — three sections in a side navigation, each listing its categories — and the
 * page header for a category restates its scope and what it touches.
 *
 * `id` is the URL `?group=` token and must stay stable: warnings and the config-health pill
 * deep-link to it. `label` is what the operator sees.
 */
export type SettingsScope = 'project' | 'nightshift' | 'browser';

export const SETTINGS_SCOPES: readonly {
  id: SettingsScope;
  label: string;
  /** One sentence on who a change here reaches. */
  reach: string;
}[] = [
  { id: 'project', label: 'Project', reach: 'Stored by Nightshift for the selected project. Other projects are untouched.' },
  { id: 'nightshift', label: 'Nightshift', reach: 'Shared by every project and every machine in this factory.' },
  { id: 'browser', label: 'This browser', reach: 'Stored in this browser only. Each machine and monitor keeps its own.' },
];

export const SETTINGS_TABS = [
  {
    id: 'prompts',
    label: 'Prompts',
    scope: 'nightshift',
    description: 'Browse stage, sidecar and supporting prompts. Edit shared templates or restore their bundled defaults.',
  },
  {
    id: 'project',
    label: 'General',
    scope: 'project',
    description: 'Register a project, or edit the selected project’s record and identity: name, key, repository, profile, capabilities and engine.',
  },
  {
    id: 'agents',
    label: 'Execution',
    scope: 'project',
    description: 'Which agents run each stage and in what order, the machine tags work requires, and the environment a machine must provide.',
  },
  {
    id: 'pipeline',
    label: 'Pipeline',
    scope: 'project',
    description:
      'Build and test commands, stage guidance, verification producers and named artefacts. GitLab CI remains the verification gate; changing these does not rewrite .gitlab-ci.yml.',
  },
  {
    id: 'merge',
    label: 'Review and merge',
    scope: 'project',
    description:
      'Whether low-risk changes may merge without waiting in Human Review, and the review cycle cap. Auto-merge is off by default; every condition must hold, and anything else still goes to a person.',
  },
  {
    id: 'performance',
    label: 'Performance',
    scope: 'project',
    description: 'Frame-rate and memory targets the pipeline holds this project to.',
  },
  {
    id: 'sidecars',
    label: 'Sidecars',
    scope: 'project',
    description:
      'Cheap background jobs that estimate what a ticket touches, compare tickets against each other and write card titles. Off until the switch is on; routes inherit Nightshift → Models unless set here.',
  },
  {
    id: 'milestones',
    label: 'Milestones',
    scope: 'project',
    description: 'The milestones tickets can be filed under.',
  },
  {
    id: 'models',
    label: 'Models',
    scope: 'nightshift',
    description:
      'The default agent routes every stage falls back to, and the utility routes cheap background jobs use. Projects and tickets narrow these; nothing here is project-specific.',
  },
  {
    id: 'routes',
    label: 'Routes',
    scope: 'nightshift',
    description:
      'Every machine, harness, model and thinking route across the product, stored or inherited. Replace a model everywhere at once, or reset projects to inherit, with a preview first. Admin only.',
  },
  {
    id: 'reviewers',
    label: 'Reviewers',
    scope: 'nightshift',
    description: 'Reviewer identities and prompts, applied to every project. Reviewer routes live under Project → Execution and Nightshift → Models.',
  },
  {
    id: 'profiles',
    label: 'Ticket type',
    scope: 'nightshift',
    description: 'Verification profiles: which checks a ticket of each type must pass. Applied to every project.',
  },
  {
    id: 'machines',
    label: 'Machines',
    scope: 'nightshift',
    description: 'The runner registry. Add a machine, download its installer, retire it.',
  },
  {
    id: 'users',
    label: 'Users',
    scope: 'nightshift',
    description: 'Accounts and roles for the whole factory.',
  },
  {
    id: 'connect',
    label: 'Connect',
    scope: 'nightshift',
    description: 'API tokens for runners and integrations.',
  },
  {
    id: 'display',
    label: 'Display',
    scope: 'browser',
    description: 'Interface scale and how times are shown.',
  },
  {
    id: 'notifications',
    label: 'Notifications',
    scope: 'browser',
    description: 'Desktop toasts for tickets waiting on you and for failures, with quiet hours.',
  },
] as const satisfies readonly {
  id: string;
  label: string;
  scope: SettingsScope;
  description: string;
}[];

export type SettingsTabId = (typeof SETTINGS_TABS)[number]['id'];
export type SettingsTab = (typeof SETTINGS_TABS)[number];

export const DEFAULT_SETTINGS_TAB: SettingsTabId = 'project';

/**
 * Retired category tokens, and where they now land.
 *
 * `concurrency` was a category with nothing behind it: no config leaf, on any layer, has ever
 * routed to it, so it could only ever render "nothing configurable here yet" under a heading
 * promising otherwise. It is gone from the navigation, but the token is still in shipped links
 * and bookmarks, so it resolves to the machine registry — the nearest screen that actually
 * governs what runs where.
 */
const SETTINGS_TAB_ALIASES: Readonly<Record<string, SettingsTabId>> = {
  concurrency: 'machines',
};

export function isSettingsTabId(value: string | null | undefined): value is SettingsTabId {
  return value !== null && value !== undefined && SETTINGS_TABS.some((tab) => tab.id === value);
}

/** The category a `?group=` token opens, following aliases, or null when it names nothing. */
export function resolveSettingsTab(value: string | null | undefined): SettingsTabId | null {
  if (isSettingsTabId(value)) return value;
  if (value !== null && value !== undefined && value in SETTINGS_TAB_ALIASES) {
    return SETTINGS_TAB_ALIASES[value]!;
  }
  return null;
}

export function settingsTab(id: SettingsTabId): SettingsTab {
  return SETTINGS_TABS.find((tab) => tab.id === id)!;
}

/** Categories under one scope, in display order. */
export function settingsTabsForScope(scope: SettingsScope): SettingsTab[] {
  return SETTINGS_TABS.filter((tab) => tab.scope === scope);
}
